import React from "react";
import { Link } from "react-router-dom";
import { Mail, Phone, MapPin, Facebook, Twitter, Instagram, Linkedin } from "lucide-react";
import BrandLogo from "@/components/BrandLogo";

const contact = {
  email: process.env.REACT_APP_CONTACT_EMAIL?.trim(),
  phone: process.env.REACT_APP_CONTACT_PHONE?.trim(),
  location: process.env.REACT_APP_CONTACT_LOCATION?.trim(),
};

const socialLinks = [
  { label: "Facebook", href: process.env.REACT_APP_FACEBOOK_URL?.trim(), Icon: Facebook },
  { label: "X / Twitter", href: process.env.REACT_APP_TWITTER_URL?.trim(), Icon: Twitter },
  { label: "Instagram", href: process.env.REACT_APP_INSTAGRAM_URL?.trim(), Icon: Instagram },
  { label: "LinkedIn", href: process.env.REACT_APP_LINKEDIN_URL?.trim(), Icon: Linkedin },
].filter(({ href }) => /^https:\/\//i.test(href || ""));

export default function Footer() {
  return (
    <footer className="premium-dark-panel mt-24 text-primary-foreground" data-testid="site-footer">
      <div className="max-w-7xl mx-auto px-6 lg:px-12 py-16 grid grid-cols-2 md:grid-cols-4 gap-12">
        <div className="col-span-2 md:col-span-2">
          <Link to="/" className="mb-6 inline-flex" aria-label="ShreeHomes home">
            {/* Footer uses the same supplied brand mark for a professional site-wide identity. */}
            <BrandLogo inverse size="lg" subtitle="The real place for real estate" />
          </Link>
          <p className="text-sm leading-relaxed text-primary-foreground/70 max-w-md mb-6">
            ShreeHomes — A real-estate marketplace for discovering published listings and connecting with registered agents and offices.
          </p>
          <div className="space-y-3 text-sm text-primary-foreground/80">
            {contact.email && <a href={`mailto:${contact.email}`} className="flex items-center gap-2 hover:text-primary-foreground transition-colors break-all"><Mail className="w-4 h-4 shrink-0"/> {contact.email}</a>}
            {contact.phone && <a href={`tel:${contact.phone.replace(/[^+\d]/g, "")}`} className="flex items-center gap-2 hover:text-primary-foreground transition-colors"><Phone className="w-4 h-4 shrink-0"/> {contact.phone}</a>}
            {contact.location && <div className="flex items-start gap-2"><MapPin className="w-4 h-4 shrink-0 mt-0.5"/> {contact.location}</div>}
            {!contact.email && !contact.phone && !contact.location && (
              <Link to="/contact" className="inline-flex items-center gap-2 hover:text-primary-foreground transition-colors">
                <Mail className="w-4 h-4 shrink-0" /> Contact support
              </Link>
            )}
          </div>
          
          {socialLinks.length > 0 && <div className="flex items-center gap-3 mt-6" aria-label="Social media links">
            {socialLinks.map(({ label, href, Icon }) => (
              <a key={label} href={href} target="_blank" rel="noreferrer noopener" className="w-8 h-8 rounded-full bg-primary-foreground/10 hover:bg-primary hover:text-primary-foreground hover:scale-105 transition-all duration-200 flex items-center justify-center text-primary-foreground/80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-foreground" aria-label={label} title={label}>
                <Icon className="w-4 h-4" />
              </a>
            ))}
          </div>}
        </div>

        <div className="col-span-1">
          <h4 className="font-display font-bold text-base mb-4">Explore</h4>
          <ul className="space-y-3 text-sm text-primary-foreground/70">
            <li><Link to="/properties" className="hover:text-primary-foreground transition-colors">Buy Property</Link></li>
            <li><Link to="/recommendations" className="hover:text-primary-foreground transition-colors">Recommendations</Link></li>
            <li><Link to="/plot-on-demand" className="hover:text-primary-foreground transition-colors">Plots on Demand</Link></li>
            <li><Link to="/agents" className="hover:text-primary-foreground transition-colors">Find Agents</Link></li>
            <li><Link to="/offices" className="hover:text-primary-foreground transition-colors">Offices</Link></li>
          </ul>
        </div>

        <div className="col-span-1">
          <h4 className="font-display font-bold text-base mb-4">Company</h4>
          <ul className="space-y-3 text-sm text-primary-foreground/70">
            <li><Link to="/about" className="hover:text-primary-foreground transition-colors">About</Link></li>
            <li><Link to="/contact" className="hover:text-primary-foreground transition-colors">Contact</Link></li>
            <li><Link to="/privacy" className="hover:text-primary-foreground transition-colors">Privacy Policy</Link></li>
            <li><Link to="/terms" className="hover:text-primary-foreground transition-colors">Terms</Link></li>
            <li><Link to="/faq" className="hover:text-primary-foreground transition-colors">FAQ</Link></li>
          </ul>
        </div>
      </div>

      <div className="border-t border-primary-foreground/10">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-6 flex flex-col md:flex-row items-center justify-between gap-3 text-xs text-primary-foreground/60 text-center md:text-left">
          <p>© {new Date().getFullYear()} ShreeHomes. All rights reserved.</p>
          <p>Crafted in India — Built for India.</p>
        </div>
      </div>
    </footer>
  );
}
